import React, { useEffect, useRef, useState } from 'react';
import { API_URL } from '../api';
import './TaskBoard.css';

// A task is done from the *agent's* side once it is completed, has the
// integration PR open/merged/closed, or failed -- that's what gates "agents are still working"
// vs "waiting on human review" in the summary line below.
const AGENT_DONE_STATUSES = ['completed', 'pr_opened', 'pr_merged', 'pr_closed', 'failed'];
// Only these mean nothing will ever change again -- pr_opened is
// deliberately excluded, since Forge keeps checking GitHub for a merge or
// close (see the polling interval logic below and the backend's
// refresh_task_pr_states).
const FULLY_TERMINAL_STATUSES = ['completed', 'pr_merged', 'pr_closed', 'failed'];

// While any task is still pending/running, poll frequently -- the agent
// loop is actively working and the user is likely watching it happen.
const ACTIVE_POLL_MS = 4000;
// Once every task has at least opened a PR (or failed), slow way down --
// all that's left to detect is a human merging/closing on GitHub in their
// own time, which doesn't need near-real-time polling.
const AWAITING_REVIEW_POLL_MS = 20000;
const TASK_REQUEST_TIMEOUT_MS = 20000;

export function taskBoardEndpoint(sessionId, userId) {
  return `${API_URL}/api/specs/${sessionId}/tasks?user_id=${userId}`;
}

export function taskBoardResponseState(response, data) {
  if (response.ok) return { tasks: Array.isArray(data) ? data : [], error: null };
  return { tasks: [], error: data?.detail || 'No fue posible cargar las tareas.' };
}

function statusMeta(status) {
  switch (status) {
    case 'pending':
      return { label: 'Pending', color: 'steel' };
    case 'running':
      return { label: 'Running', color: 'amber' };
    case 'completed':
      return { label: 'Task completed', color: 'blue' };
    case 'pr_opened':
      return { label: 'PR opened', color: 'green' };
    case 'pr_merged':
      return { label: 'Merged', color: 'violet' };
    case 'pr_closed':
      return { label: 'Closed (not merged)', color: 'steel' };
    case 'failed':
      return { label: 'Failed', color: 'red' };
    default:
      return { label: status, color: 'steel' };
  }
}

function nextTaskAction(task, prerequisite) {
  if (task.status === 'failed') return task.pr_url ? 'Revisar la PR existente antes de continuar.' : 'Reintentar esta tarea o escalar el bloqueo.';
  if (task.status === 'completed') return 'Esperar la PR integradora de la Forge Spec.';
  if (task.status === 'pr_opened') return 'Revisar y fusionar la Pull Request.';
  if (task.status === 'pr_merged') return 'Confirmar la evidencia de CI/CD en Entregar.';
  if (task.status === 'pr_closed') return 'Definir si se corrige o se replantea la tarea.';
  if (prerequisite && !AGENT_DONE_STATUSES.includes(prerequisite.status)) return `Esperar la tarea ${task.depends_on_task_index + 1}.`;
  return task.status === 'running' ? 'FORGE está construyendo esta tarea.' : 'FORGE iniciará esta tarea al cumplir sus dependencias.';
}

// Polls GET /api/specs/{sessionId}/tasks on an interval and renders one
// card per AgentTask row. Owns its own fetch/poll lifecycle (rather than
// having the parent SpecFlow page manage it) so mounting/unmounting this
// component -- which SpecFlow does purely based on session.status ===
// 'approved' -- is enough to start/stop polling, no extra wiring needed.
// Polling never fully stops once any task has opened a PR without also
// having merged/closed -- the backend re-checks GitHub's real PR state on
// every call (see refresh_task_pr_states), so this is how a merge or close
// done directly on GitHub shows up here without any user action, just at
// a slower cadence once it's waiting on a human rather than an agent.
function TaskBoard({ sessionId, userId, onRetryTask }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const scheduleNext = (delayMs) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(fetchTasks, delayMs);
    };

    const fetchTasks = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), TASK_REQUEST_TIMEOUT_MS);
      try {
        const res = await fetch(taskBoardEndpoint(sessionId, userId), {
          credentials: 'include', signal: controller.signal,
        });
        const data = await res.json();
        if (cancelled) return;

        const nextState = taskBoardResponseState(res, data);
        setTasks(nextState.tasks);
        setError(nextState.error);
        if (!nextState.error) {
          const allFullyTerminal = nextState.tasks.length > 0 && nextState.tasks.every((t) => FULLY_TERMINAL_STATUSES.includes(t.status));
          if (!allFullyTerminal) {
            const stillActive = nextState.tasks.some((t) => !AGENT_DONE_STATUSES.includes(t.status));
            scheduleNext(stillActive ? ACTIVE_POLL_MS : AWAITING_REVIEW_POLL_MS);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err?.name === 'AbortError' ? 'La carga de tareas tardó demasiado. Vuelve a intentar en unos segundos.' : 'Error de red: verifica que el backend esté disponible.');
      } finally {
        clearTimeout(timeout);
        if (!cancelled) setLoading(false);
      }
    };

    fetchTasks();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId, userId]);

  if (loading) {
    return <div className="task-board-empty">Cargando tareas y sincronizando su estado…</div>;
  }

  if (error) {
    return <div className="tb-message error">{error}</div>;
  }

  if (tasks.length === 0) {
    return <div className="task-board-empty">No tasks yet.</div>;
  }

  const agentsStillWorking = tasks.some((t) => !AGENT_DONE_STATUSES.includes(t.status));
  const merged = tasks.filter((t) => t.status === 'pr_merged').length;
  const openForReview = tasks.filter((t) => t.status === 'pr_opened').length;
  const completed = tasks.filter((t) => t.status === 'completed').length;
  const failed = tasks.filter((t) => t.status === 'failed').length;

  return (
    <div className="task-board">
      <div className="task-board-summary">
        {agentsStillWorking ? (
          <span className="task-board-live">
            <span className="live-dot" />
            Agents working — {completed + merged + openForReview + failed}/{tasks.length} done
          </span>
        ) : (
          <span>
            {completed}/{tasks.length} tasks completed
            {openForReview > 0 ? `, 1 integration PR awaiting review` : ''}
            {merged > 0 ? ', integration PR merged' : ''}
            {failed > 0 ? `, ${failed} failed` : ''}
          </span>
        )}
      </div>

      <div className="task-board-grid">
        {tasks.map((task, index) => {
          const meta = statusMeta(task.status);
          const prerequisite =
            task.depends_on_task_index != null ? tasks[task.depends_on_task_index] : null;
          const waitingOnPrereq = prerequisite && !AGENT_DONE_STATUSES.includes(prerequisite.status);

          return (
            <div key={task.id} className="task-card">
              <div className="task-card-top">
                <span className="task-index">Task {index + 1}</span>
                <span className={`status-pill ${meta.color}`}>
                  <span className="status-dot" />
                  {meta.label}
                </span>
              </div>

              <h3 className="task-title">{task.title}</h3>

              {prerequisite && (
                <div className="task-depends">
                  ↳ builds on Task {task.depends_on_task_index + 1}
                  {waitingOnPrereq ? ' (waiting)' : ''}
                </div>
              )}

              <div className="task-responsibility">Responsable: Builder Worker</div>

              <p className="task-description">{task.description}</p>

              {task.branch_name && <div className="task-branch">{task.branch_name}</div>}
              {task.commit_sha && <div className="task-branch">Commit: {task.commit_sha}</div>}

              {task.pr_url && (
                <a href={task.pr_url} target="_blank" rel="noopener noreferrer" className="task-pr-link">
                  {task.status === 'pr_merged' ? '✓ Integration PR merged' : `View integration PR #${task.pr_number}`} →
                </a>
              )}

              {task.result_note && <div className={task.status === 'failed' ? 'task-error' : 'task-result'}>{task.result_note}</div>}

              <small className="task-next-action">Siguiente: {nextTaskAction(task, prerequisite)}</small>
              {task.status === 'failed' && !task.pr_url && onRetryTask && <button type="button" className="task-retry" onClick={() => onRetryTask(task)}>Reintentar tarea</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TaskBoard;
